/**
 * DJLiveLobby — dj-live-lobby.
 *
 * The durable social hub for live Skibidi / streaming sessions. Wide playable
 * surface, thick buffer under-floor (100 tiles deep) + side buffers (100 tiles
 * each) so fall-off / out-of-bounds physics glitches can't launch anyone into
 * the void. High ceiling (99 tiles of headroom) for jumps, knockbacks, editor
 * tests, and future vertical gameplay.
 *
 * Layout (tile coords, y grows downward):
 *
 *     x=0 ────────── x=299
 *   y=0  ┌───────────────────┐
 *        │                   │  ← 99 tiles of ceiling / air
 *        │                   │
 *        │    ▣▣▣▣▣▣▣▣       │  ← spawn platform at y=92
 *        │     (8t wide)     │    centered on x=146..153
 *        │         ·         │  ← player spawn at (150, 92) —
 *        │                   │    7 tiles above ground surface
 *   y=99 ├━━━━━━━━━━━━━━━━━━━┤  ← ground SURFACE (top of buffer)
 *        │░░░░░░░░░░░░░░░░░░░│
 *        │░  buffer mass    ░│  ← 100 tiles of solid ground beneath
 *        │░   (300×100)     ░│
 *  y=199 └░░░░░░░░░░░░░░░░░░░┘
 *
 * The full ground mass is 300t × 100t (one rectangle); the entire playable
 * width is solid floor, so walking off the "intended" middle 100-tile range
 * leaves you still on ground. Side buffer mass absorbs any fall-off.
 *
 * Use: social lobby, stream viewer landings, editor-tile testing. Shipped
 * 2026-04-20 for `make tunnel-lobby`.
 */

import { PlatformerLevel } from '../../shared/CharacterTypes';
import { GridLevel, LayerType } from '../../shared/TileEditorTypes';

const MAP_ID = 'dj-live-lobby';
const MAP_NAME = 'DJ Live Lobby';
const VERSION = '1.0';

const T = 32;         // tile size (px)
const W = 300;        // width in tiles (100 buffer + 100 play + 100 buffer)
const H = 200;        // height in tiles (100 ceiling + 100 ground buffer)

const GROUND_TOP_ROW = 99;    // y-tile where ground SURFACE begins
const GROUND_DEPTH = 100;     // tiles of solid ground beneath the surface
const SPAWN_PLATFORM_ROW = GROUND_TOP_ROW - 7;  // 7 tiles above ground = 92
const SPAWN_PLATFORM_WIDTH = 8;                  // 8 tiles wide
const PLAY_CENTER_X = 150;    // centerline of the intended play area
const SPAWN_PLATFORM_X = PLAY_CENTER_X - SPAWN_PLATFORM_WIDTH / 2;  // 146

// ============================================================================
// SPAWN CONFIGURATION
// ============================================================================
//
// Spawn point lands ON the loading platform (tile 150, 92). Player falls onto
// it when joining. From there they can walk off the edge and drop the 7 tiles
// onto the ground surface below.

const spawnPoints = [
  { x: PLAY_CENTER_X, y: SPAWN_PLATFORM_ROW },
];

const defaultSpawn = {
  x: PLAY_CENTER_X * T,
  y: SPAWN_PLATFORM_ROW * T,
};

// ============================================================================
// RUNTIME PLATFORMS (collision geometry)
// ============================================================================
//
// Two rectangles: one huge ground buffer mass, one small spawn platform.
// Using explicit platforms instead of 30,000 tile entries in the GROUND layer
// is far more efficient — auto-generation would chunk them into many adjacent
// rectangles; our single-rect form is flat and fast.

const runtimePlatforms = [
  {
    id: 'ground-mass',
    x: 0,
    y: GROUND_TOP_ROW * T,
    width: W * T,
    height: GROUND_DEPTH * T,
    type: 'normal' as const,
    color: '#5a4a3a',  // earthy brown
  },
  {
    id: 'spawn-platform',
    x: SPAWN_PLATFORM_X * T,
    y: SPAWN_PLATFORM_ROW * T,
    width: SPAWN_PLATFORM_WIDTH * T,
    height: T,
    type: 'normal' as const,
    color: '#8e44ad',  // purple — stands out against the brown ground
  },
];

// ============================================================================
// PARALLAX / VISUAL
// ============================================================================
//
// Two-band parallax gives a hint of depth without committing to an art pass.
// Replace / extend in the editor later.


// ============================================================================
// EXPORT
// ============================================================================

export const DJLiveLobby: GridLevel & Partial<PlatformerLevel> = {
  id: MAP_ID,
  name: MAP_NAME,
  version: VERSION,

  widthTiles: W,
  heightTiles: H,
  width: W * T,
  height: H * T,
  tileSize: T,

  backgroundColor: '#08080f',
  gravity: 1000,

  spawnPoints,
  defaultSpawn,


  // Tile layers kept sparse — visual is driven by the runtime platforms above.
  // Editing in the in-game editor will populate these if you want painted tiles.
  layers: [
    { name: LayerType.GROUND,    visible: true, locked: false, opacity: 1.0, tiles: new Map() },
    { name: LayerType.COLLISION, visible: true, locked: false, opacity: 1.0, tiles: new Map() },
    { name: LayerType.OBJECTS,   visible: true, locked: false, opacity: 1.0, tiles: new Map() },
    { name: LayerType.ENTITIES,  visible: true, locked: false, opacity: 1.0, tiles: new Map(), entities: [] },
    { name: LayerType.TRIGGERS,  visible: true, locked: false, opacity: 1.0, tiles: new Map() },
  ],

  platforms: runtimePlatforms,
  entities: [],
  spawnRules: [],
  type: 'town',

  metadata: {
    author: 'DJ',
    description: 'Live lobby for Skibidi / stream sessions. 300×200, 100t ground buffer, high ceiling, spawn platform 7t above ground.',
    tags: ['lobby', 'social', 'testing', 'skibidi'],
    // Opt-in to player-facing Lobby Tools (spawn dummies, bots, self-heal).
    // The server-side authoritative allow-list is
    // `LOBBY_TOOLS_MAP_REGISTRY` in `src/shared/constants/LobbyTools.ts`;
    // this field is the per-map declaration that belongs next to the map
    // content itself.
    lobbyTools: { enabled: true },
    // Map rule profile — `lobby_safe` is the social-hub template
    // (PvP off, fast respawn, no death penalty, ignore bots). DJ-live
    // overrides two knobs:
    //
    //   - pvp.enabled: true → players can brawl in the lobby
    //   - pvp.botBehavior: 'attack-all' → bots from Lobby Tools fight
    //     every player including their summoner, so DJ can solo-test
    //     the brawl loop end-to-end (spawn bot → bot attacks DJ → DJ
    //     dies + respawns). 'attack-non-owner' is the right default
    //     once a second human joins; for solo lobby use 'attack-all'
    //     keeps the loop self-contained. Engine supports both — flip
    //     this string to switch.
    //
    // friendlyFire stays at the lobby_safe default ('off') so even
    // with PvP enabled, two humans don't damage each other unless a
    // future override flips it to 'all'.
    profile: 'lobby_safe',
    overrides: {
      pvp: { enabled: true, botBehavior: 'attack-all' },
    },
  },
};

export { W, H, T, MAP_ID, MAP_NAME, GROUND_TOP_ROW, SPAWN_PLATFORM_ROW, PLAY_CENTER_X };
